import { CONFIG } from "../../../../config";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";

const CRMCONFIG = CONFIG.modules["crm"];
const API_HOST_CRM = CRMCONFIG.host;

const isDevelopment = process.env.NODE_ENV === 'development';
const devLog = (message, data) => {
  if (isDevelopment) {
    console.log(message, data);
  }
};

export const useExportDailyCallAnalytics = (options = {}) => {
  const { onSuccess = () => {}, onError = () => {} } = options;

  const downloadFile = useCallback((blob, filename) => {
    try {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      devLog('[useExportDailyCallAnalytics] File downloaded successfully:', filename);
    } catch (error) {
      console.error('[useExportDailyCallAnalytics] Download failed:', error);
      throw new Error('Failed to download file');
    }
  }, []);

  const exportDailyCallAnalytics = useCallback(async (params = {}) => {
    try {
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([, value]) =>
          value !== undefined && value !== null && value !== ''
        )
      );

      devLog('[useExportDailyCallAnalytics] Making export request with params:', cleanParams);

      const response = await axios.get(`${API_HOST_CRM}/analytics/exports/daily-call-analytics`, {
        params: cleanParams,
        responseType: 'blob',
        timeout: 60000,
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });

      const contentDisposition = response.headers['content-disposition'];
      let filename = 'daily-call-analytics-export.xlsx';

      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      downloadFile(blob, filename);

      devLog('[useExportDailyCallAnalytics] Export completed successfully');

      return { success: true, filename };
    } catch (error) {
      const isNetworkError = !error.response && error.code === 'ENOTFOUND';
      const isTimeoutError = error.code === 'ECONNABORTED';

      let errorMessage = 'Error exporting daily call analytics';

      if (isNetworkError) {
        errorMessage += ': Network connection failed';
      } else if (isTimeoutError) {
        errorMessage += ': Export timeout - file may be too large';
      } else if (error.response) {
        errorMessage += `: Server responded with ${error.response.status}`;
      }

      console.error(errorMessage, { originalError: error, params, timestamp: new Date().toISOString() });

      throw new Error(errorMessage);
    }
  }, [downloadFile]);

  return useMutation({
    mutationFn: exportDailyCallAnalytics,
    onSuccess: (data, variables) => {
      devLog('[useExportDailyCallAnalytics] Export mutation succeeded:', data);
      onSuccess(data, variables);
    },
    onError: (error, variables) => {
      console.error('Error in useExportDailyCallAnalytics mutation:', { error: error.message, params: variables, timestamp: new Date().toISOString() });
      onError(error, variables);
    }
  });
};
